/**
 * SimplifiedCaretSystem - Streamlined caret positioning coordinator
 * 
 * Integrates 4 core components into simple, reliable caret system:
 * - PrecisionHitTestingEngine for accurate positioning
 * - SmartCaretManager for intelligent behavior
 * - CaretRenderingEngine for visual presentation
 * - CaretPerformanceMonitor for performance tracking
 * 
 * Phase 1 Implementation: Core system integration
 */

import { PrecisionHitTestingEngine } from './engines/PrecisionHitTestingEngine';
import { SmartCaretManager, CaretState, CaretContext } from './managers/SmartCaretManager';
import { CaretRenderingEngine } from './rendering/CaretRenderingEngine';
import { CaretPerformanceMonitor } from './monitoring/CaretPerformanceMonitor';
import { CaretAnimationSystem, DEFAULT_BLINKING_CONFIG } from './animation/CaretAnimationSystem';
import { CaretPosition } from './interfaces/CaretInterfaces';
import { FrontendTextPositionCalculator, FontMetrics, DocumentMetrics } from './positioning/FrontendTextPositionCalculator';
import { CoordinateSpace } from './core/UnifiedCoordinateSystem';
import { ChangeMetadata } from '../components/PaperCanvas';

export interface SimplifiedCaretConfig {
  enableHighDPI: boolean;
  enableSubPixelRendering: boolean;
  enableAnimations: boolean;
  enableContextAwareStyling: boolean;
  enableBlinking: boolean;
  debugMode: boolean;
}

export interface SimplifiedCaretMetrics {
  positioningTime: number;
  renderingTime: number;
  cacheHitRate: number;
  totalOperations: number;
  averageConfidence: number;
}

export interface CaretPositioningOptions {
  useFrontendText?: boolean;
  frontendText?: string;
  fontMetrics?: FontMetrics;
  documentMetrics?: DocumentMetrics;
}

export class SimplifiedCaretSystem {
  private hitTestingEngine: PrecisionHitTestingEngine;
  private caretManager: SmartCaretManager;
  private renderingEngine: CaretRenderingEngine | null = null;
  private performanceMonitor: CaretPerformanceMonitor;
  private blinkingSystem: CaretAnimationSystem;
  private frontendPositionCalculator: FrontendTextPositionCalculator;
  
  // System state
  private currentPosition: CaretPosition | null = null;
  private isVisible: boolean = true;
  private isInitialized: boolean = false;
  private config: SimplifiedCaretConfig;
  
  
  // Performance optimization
  private positionCache: Map<string, CaretPosition> = new Map();
  private characterIndexCache: Map<number, CaretPosition> = new Map();
  private maxCacheSize: number = 200; // Increased for better performance
  private cacheHits: number = 0;
  private cacheMisses: number = 0;
  
  // Incremental positioning for speed
  private lastKnownPosition: CaretPosition | null = null;
  private textLength: number = 0;
  private layoutPlanHash: string = '';
  
  // Deferred initialization state
  private pendingInitialization: {
    canvas: HTMLCanvasElement;
    initialCaretPosition?: number;
  } | null = null;
  
  constructor(config: Partial<SimplifiedCaretConfig> = {}) {
    this.config = {
      enableHighDPI: true,
      enableSubPixelRendering: true,
      enableAnimations: true,
      enableContextAwareStyling: true,
      enableBlinking: true,
      debugMode: false,
      ...config
    };

    this.hitTestingEngine = new PrecisionHitTestingEngine();
    this.caretManager = new SmartCaretManager(this.hitTestingEngine, {
      coordinateSpace: 'CANVAS' as any,
      precision: 'subpixel',
      performance: 'optimized',
      enableAnimations: this.config.enableAnimations,
      enableBlinking: true,
      enableKeyboardNavigation: true,
      hitTestTolerance: 3,
      debugMode: this.config.debugMode,
      enableAccessibility: true,
      features: {
        caching: true,
        spatialIndexing: true,
        animations: this.config.enableAnimations,
        legalDocumentSupport: true,
        performanceMonitoring: true
      },
      thresholds: {
        hitTestTimeout: 10,
        cacheSize: 1000,
        spatialIndexDepth: 8
      }
    });
    this.performanceMonitor = new CaretPerformanceMonitor();
    this.blinkingSystem = new CaretAnimationSystem({
      ...DEFAULT_BLINKING_CONFIG,
      enabled: this.config.enableBlinking,
      debugMode: this.config.debugMode
    });
    this.frontendPositionCalculator = new FrontendTextPositionCalculator();

  }
  
  // ============================================================================
  // SYSTEM INITIALIZATION
  // ============================================================================
  
  /**
   * Initialize system with canvas and layout plan
   */
  async initialize(canvas: HTMLCanvasElement, layoutPlan: any, initialCaretPosition?: number): Promise<void> {
    const endTiming = this.performanceMonitor.startOperation('systemInit');
    
    try {
      console.log('[SimplifiedCaretSystem] Starting initialization...');
      
      // Initialize rendering engine (always needed)
      this.renderingEngine = new CaretRenderingEngine(canvas, {
        enableHighDPI: this.config.enableHighDPI,
        enableSubPixelRendering: this.config.enableSubPixelRendering,
        debugMode: this.config.debugMode,
        enableAccessibilityIndicator: true
      });
      
      // CRITICAL: Only initialize with layout plan if it's valid
      if (this.isValidLayoutPlan(layoutPlan)) {
        await this.initializeWithLayoutPlan(layoutPlan, initialCaretPosition);
        this.isInitialized = true;
        console.log('[SimplifiedCaretSystem] System initialized successfully with layout plan');
        
        // Initialize blinking system if enabled and we have a position
        if (this.config.enableBlinking && this.currentPosition) {
          this.blinkingSystem.initialize(canvas, {
            x: this.currentPosition.coordinates.x,
            y: this.currentPosition.coordinates.y,
            height: this.extractFontSizeFromLayoutPlan() || 20
          });
        }
      } else {
        console.log('[SimplifiedCaretSystem] Layout plan not ready, deferring initialization');
        this.isInitialized = false; // Keep uninitialized until layout plan arrives
        this.pendingInitialization = { canvas, initialCaretPosition };
      }
      
    } catch (error) {
      console.error('[SimplifiedCaretSystem] Initialization failed:', error);
      this.isInitialized = false;
      throw error;
    } finally {
      endTiming();
    }
  }
  
  /**
   * Validate that layout plan has required structure for initialization
   */
  private isValidLayoutPlan(layoutPlan: any): boolean {
    if (!layoutPlan) return false;
    
    const actualLayoutPlan = layoutPlan?.data?.layoutPlan || layoutPlan;
    
    // Check for required nested structure
    const hasValidStructure = !!(
      actualLayoutPlan?.pages?.[0]?.frames?.[0]?.lines &&
      Array.isArray(actualLayoutPlan.pages[0].frames[0].lines) &&
      actualLayoutPlan.pages[0].frames[0].lines.length > 0
    );
    
    console.log('[SimplifiedCaretSystem] Layout plan validation:', {
      hasLayoutPlan: !!layoutPlan,
      hasActualLayoutPlan: !!actualLayoutPlan,
      hasValidStructure,
      linesCount: actualLayoutPlan?.pages?.[0]?.frames?.[0]?.lines?.length || 0
    });
    
    return hasValidStructure;
  }
  
  /**
   * Set initial caret position from character index
   */
  private async setInitialCaretPosition(charIndex: number, layoutPlan: any): Promise<void> {
    console.log('[SimplifiedCaretSystem] Setting initial caret position:', { charIndex, hasLayoutPlan: !!layoutPlan });
    
    try {
      // If charIndex is at the last character, position after it
      const textLength = this.extractTextLength(layoutPlan);
      if (charIndex > textLength - 1) {
        console.log('[SimplifiedCaretSystem] Positioning after last character:', {
          charIndex,
          textLength,
          positioningAfter: true
        });
        
        // Position after the last character
        const position = this.findCharacterPositionInLayoutPlan(textLength, layoutPlan);
        if (position) {
          this.currentPosition = position;
          this.isVisible = true;
          console.log('[SimplifiedCaretSystem] Initial caret position set (after last char):', {
            characterIndex: position.index,
            coordinates: position.coordinates,
            lineIndex: position.lineIndex,
            isVisible: this.isVisible
          });
          return;
        }
      }
      
      // Find the character position using high-performance lookup
      console.log('[SimplifiedCaretSystem] setInitialCaretPosition debug:', {
        charIndex,
        hasLayoutPlan: !!layoutPlan,
        layoutPlanType: typeof layoutPlan,
        hasPages: !!layoutPlan?.pages,
        pagesLength: layoutPlan?.pages?.length || 0,
        hasFrames: !!layoutPlan?.pages?.[0]?.frames,
        framesLength: layoutPlan?.pages?.[0]?.frames?.length || 0,
        hasLines: !!layoutPlan?.pages?.[0]?.frames?.[0]?.lines,
        linesLength: layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.length || 0,
        firstLineRuns: layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.length || 0,
        firstRunText: layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0]?.text || 'no text',
        firstRunCharBoxes: layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0]?.charBoxes?.length || 0
      });
      
      const position = this.findCharacterPositionFast(charIndex, layoutPlan);
      
      if (position) {
        this.currentPosition = position;
        this.isVisible = true;
        
        console.log('[SimplifiedCaretSystem] Initial caret position set:', {
          characterIndex: charIndex,
          coordinates: position.coordinates,
          lineIndex: position.lineIndex,
          isVisible: this.isVisible
        });
      } else {
        console.warn('[SimplifiedCaretSystem] Could not find initial caret position for index:', charIndex);
        // Set a fallback position
        this.currentPosition = this.getFallbackPosition(0, 0);
        this.currentPosition.index = charIndex;
        this.isVisible = true;
        
        console.log('[SimplifiedCaretSystem] Using fallback position:', {
          characterIndex: charIndex,
          coordinates: this.currentPosition.coordinates,
          isVisible: this.isVisible
        });
      }
    } catch (error) {
      console.error('[SimplifiedCaretSystem] Failed to set initial caret position:', error);
      // Set fallback position
      this.currentPosition = this.getFallbackPosition(0, 0);
      this.currentPosition.index = charIndex;
      this.isVisible = true;
      
      console.log('[SimplifiedCaretSystem] Using error fallback position:', {
        characterIndex: charIndex,
        coordinates: this.currentPosition.coordinates,
        isVisible: this.isVisible
      });
    }
  }
  
  /**
   * Initialize with layout plan (extracted from main initialize method)
   */
  private async initializeWithLayoutPlan(layoutPlan: any, initialCaretPosition?: number): Promise<void> {
    const actualLayoutPlan = layoutPlan?.data?.layoutPlan || layoutPlan;
    
    const planHash = this.generateLayoutPlanHash(actualLayoutPlan);
    this.hitTestingEngine.updateLayoutPlan(actualLayoutPlan, planHash);
    
    if (this.config.debugMode) {
      console.log('[SimplifiedCaretSystem] Layout plan updated:', planHash.slice(0, 8));
    }
    
    // Set initial caret position if provided
    console.log('[SimplifiedCaretSystem] Initial caret position parameter:', initialCaretPosition);
    if (initialCaretPosition !== undefined) {
      await this.setInitialCaretPosition(initialCaretPosition, actualLayoutPlan);
    } else {
      console.log('[SimplifiedCaretSystem] No initial caret position provided, setting default position 0');
      await this.setInitialCaretPosition(0, actualLayoutPlan);
    }
    
    // Extract text length for performance optimization
    this.textLength = this.extractTextLength(actualLayoutPlan);
  }
  
  /**
   * High-performance character position lookup with caching
   */
  private findCharacterPositionFast(charIndex: number, layoutPlan: any): CaretPosition | null {
    const startTime = performance.now();
    
    // DEBUG: Character position lookup debugging
    console.log('[SimplifiedCaretSystem] findCharacterPositionFast DEBUG:', {
      charIndex,
      hasLayoutPlan: !!layoutPlan,
      layoutPlanType: typeof layoutPlan,
      hasPages: !!layoutPlan?.pages,
      pageCount: layoutPlan?.pages?.length || 0,
      cacheSize: this.characterIndexCache.size,
      hasCachedPosition: this.characterIndexCache.has(charIndex),
      lastKnownPosition: this.lastKnownPosition?.index,
      timestamp: performance.now()
    });
    
    // Check character index cache first (fastest)
    if (this.characterIndexCache.has(charIndex)) {
      this.cacheHits++;
      const cached = this.characterIndexCache.get(charIndex)!;
      console.log('[SimplifiedCaretSystem] Character index cache hit:', {
        charIndex,
        cached: cached.coordinates,
        lookupTime: performance.now() - startTime
      });
      return cached;
    }
    
    // Check if we can use incremental positioning (very fast)
    if (this.lastKnownPosition && this.canUseIncrementalPositioning(charIndex)) {
      const incremental = this.getIncrementalPosition(charIndex);
      if (incremental) {
        this.cacheCharacterPosition(charIndex, incremental);
        this.cacheHits++;
        console.log('[SimplifiedCaretSystem] Incremental positioning:', {
          charIndex,
          incremental: incremental.coordinates,
          lookupTime: performance.now() - startTime
        });
        return incremental;
      }
    }
    
    // Fall back to full layout plan traversal (slowest)
    this.cacheMisses++;
    const position = this.findCharacterPositionInLayoutPlan(charIndex, layoutPlan);
    
    if (position) {
      this.cacheCharacterPosition(charIndex, position);
    }
    
    console.log('[SimplifiedCaretSystem] Full layout lookup:', {
      charIndex,
      position: position?.coordinates,
      lookupTime: performance.now() - startTime
    });
    
    return position;
  }

  /**
   * Check if we can use incremental positioning for speed
   */
  private canUseIncrementalPositioning(charIndex: number): boolean {
    if (!this.lastKnownPosition) return false;
    
    const lastIndex = this.lastKnownPosition.index;
    const distance = Math.abs(charIndex - lastIndex);
    
    // Use incremental for small movements (within 10 characters)
    return distance <= 10 && charIndex >= 0 && charIndex < this.textLength;
  }

  /**
   * Get position using incremental calculation (very fast)
   */
  private getIncrementalPosition(charIndex: number): CaretPosition | null {
    if (!this.lastKnownPosition) return null;
    
    const lastIndex = this.lastKnownPosition.index;
    const lastCoords = this.lastKnownPosition.coordinates;
    const delta = charIndex - lastIndex;
    
    // Simple incremental calculation based on character width
    const charWidth = 8; // Approximate character width
    const newX = lastCoords.x + (delta * charWidth);
    
    return {
      index: charIndex,
      coordinates: {
        x: newX,
        y: lastCoords.y,
        space: lastCoords.space,
        precision: lastCoords.precision,
        timestamp: performance.now()
      },
      lineIndex: this.lastKnownPosition.lineIndex,
      runIndex: this.lastKnownPosition.runIndex,
      charInRun: this.lastKnownPosition.charInRun + delta,
      context: this.lastKnownPosition.context,
      metadata: {
        method: 'optimization',
        confidence: 0.8, // Lower confidence for incremental
        timing: { totalTime: 0 },
        debugInfo: { fallbackReason: 'incremental_positioning' }
      }
    };
  }

  /**
   * Cache character position for future lookups
   */
  private cacheCharacterPosition(charIndex: number, position: CaretPosition): void {
    this.characterIndexCache.set(charIndex, position);
    this.lastKnownPosition = position;
    
    // Limit cache size
    if (this.characterIndexCache.size > this.maxCacheSize) {
      const firstKey = this.characterIndexCache.keys().next().value;
      if (firstKey !== undefined) {
        this.characterIndexCache.delete(firstKey);
      }
    }
  }

  /**
   * Find character position in layout plan (fallback method)
   */
  private findCharacterPositionInLayoutPlan(charIndex: number, layoutPlan: any): CaretPosition | null {
    console.log('[SimplifiedCaretSystem] Finding character position:', { charIndex, hasLayoutPlan: !!layoutPlan });
    
    // Handle nested structure: data.layoutPlan.pages[0].frames[0].lines
    const actualLayoutPlan = layoutPlan?.data?.layoutPlan || layoutPlan;
    
    // DEBUG: Comprehensive layout plan structure analysis
    console.log('[SimplifiedCaretSystem] Layout plan analysis:', {
      charIndex,
      hasLayoutPlan: !!layoutPlan,
      hasData: !!layoutPlan?.data,
      hasDataLayoutPlan: !!layoutPlan?.data?.layoutPlan,
      hasActualLayoutPlan: !!actualLayoutPlan,
      actualLayoutPlanType: typeof actualLayoutPlan,
      actualLayoutPlanKeys: actualLayoutPlan ? Object.keys(actualLayoutPlan) : [],
      timestamp: performance.now()
    });
    
    // Check for lines at different possible locations
    let lines = null;
    
    // NOTE: Backend only creates nested pages[0].frames[0].lines structure
    // Top-level lines structure is not created by backend TypesetterService
    // if (actualLayoutPlan?.lines) {
    //   // Lines at top level (current structure)
    //   lines = actualLayoutPlan.lines;
    //   console.log('[SimplifiedCaretSystem] Found lines at top level:', { linesCount: lines.length });
    // } else 
    
    if (actualLayoutPlan?.pages?.[0]?.frames?.[0]?.lines) {
      // Lines nested under pages[0].frames[0] (expected structure from backend)
      lines = actualLayoutPlan.pages[0].frames[0].lines;
      console.log('[SimplifiedCaretSystem] Found lines under pages[0].frames[0]:', { linesCount: lines.length });
    } else {
      console.log('[SimplifiedCaretSystem] No lines found in layout plan:', {
        hasData: !!layoutPlan?.data,
        hasLayoutPlan: !!layoutPlan?.data?.layoutPlan,
        hasPages: !!actualLayoutPlan?.pages,
        pagesLength: actualLayoutPlan?.pages?.length || 0,
        hasFrames: !!actualLayoutPlan?.pages?.[0]?.frames,
        framesLength: actualLayoutPlan?.pages?.[0]?.frames?.length || 0,
        hasLines: !!actualLayoutPlan?.pages?.[0]?.frames?.[0]?.lines,
        linesLength: actualLayoutPlan?.pages?.[0]?.frames?.[0]?.lines?.length || 0,
        hasTopLevelLines: !!actualLayoutPlan?.lines,
        topLevelLinesLength: actualLayoutPlan?.lines?.length || 0
      });
      return null;
    }

    let currentIndex = 0;
    
    // Check if lines is iterable
    if (!Array.isArray(lines)) {
      console.log('[SimplifiedCaretSystem] Lines is not an array:', { 
        lines, 
        type: typeof lines,
        constructor: lines?.constructor?.name,
        isIterable: typeof lines?.[Symbol.iterator] === 'function'
      });
      return null;
    }
    
    console.log('[SimplifiedCaretSystem] Layout plan structure:', {
      linesCount: lines.length,
      firstLineRuns: lines[0]?.runs?.length || 0,
      firstRunCharBoxes: lines[0]?.runs?.[0]?.charBoxes?.length || 0,
      firstRunText: lines[0]?.runs?.[0]?.text || 'no text',
      targetCharIndex: charIndex
    });
    
    // DEBUG: Calculate total character count in layout plan
    let totalCharacters = 0;
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      for (let runIndex = 0; runIndex < (line.runs || []).length; runIndex++) {
        const run = line.runs[runIndex];
        if (run?.charBoxes) {
          totalCharacters += run.charBoxes.length;
        }
      }
    }
    
    console.log('[SimplifiedCaretSystem] Character count analysis:', {
      targetCharIndex: charIndex,
      totalCharactersInLayout: totalCharacters,
      isTargetIndexValid: charIndex >= 0 && charIndex <= totalCharacters,
      willFindPosition: charIndex <= totalCharacters
    });

    // Handle empty document case
    if (totalCharacters === 0) {
      console.log('[SimplifiedCaretSystem] Empty document detected, providing default position');
      
      // Get document start position from layout plan
      const firstFrame = actualLayoutPlan?.pages?.[0]?.frames?.[0];
      const contentX = firstFrame?.x || 24; // Default to left margin
      const contentY = firstFrame?.y || 24; // Default to top margin
      
      return {
        index: 0,
        coordinates: { 
          x: contentX, 
          y: contentY, 
          space: 'CANVAS' as any, 
          precision: 'subpixel',
          timestamp: performance.now()
        },
        lineIndex: 0,
        runIndex: 0,
        charInRun: 0,
        context: {
          isWordStart: true,
          isWordEnd: true,
          isLineStart: true,
          isLineEnd: true
        },
        metadata: {
          method: 'fallback',
          confidence: 1.0,
          timing: { totalTime: 0 },
          debugInfo: { fallbackReason: 'empty_document_default' }
        }
      };
    }

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      
      for (let runIndex = 0; runIndex < (line.runs || []).length; runIndex++) {
        const run = line.runs[runIndex];
        
        if (!run?.charBoxes || !run?.text) {
          const skipCount = run?.text?.length || 0;
          console.log('[SimplifiedCaretSystem] Skipping run without charBoxes:', { 
            lineIndex, 
            runIndex, 
            skipCount, 
            currentIndex, 
            targetCharIndex: charIndex,
            willSkip: currentIndex + skipCount < charIndex
          });
          currentIndex += skipCount;
          continue;
        }
        
        // DEBUG: Track character progression through runs
        console.log('[SimplifiedCaretSystem] Processing run:', {
          lineIndex,
          runIndex,
          runText: run.text,
          runTextLength: run.text.length,
          charBoxesLength: run.charBoxes.length,
          currentIndex,
          targetCharIndex: charIndex,
          willProcessChars: currentIndex <= charIndex && charIndex < currentIndex + run.charBoxes.length
        });
        
        for (let charInRun = 0; charInRun < run.charBoxes.length; charInRun++) {
          
          // DEBUG: Track character index progression
          if (currentIndex % 5 === 0 || currentIndex === charIndex) { // Log every 5th character or target character
            console.log('[SimplifiedCaretSystem] Character index progression:', {
              currentIndex,
              targetCharIndex: charIndex,
              charInRun,
              runText: run.text,
              charBox: run.charBoxes[charInRun],
              isTarget: currentIndex === charIndex
            });
          }
          
          if (currentIndex === charIndex) {
            const charBox = run.charBoxes[charInRun];
            
            console.log('[SimplifiedCaretSystem] FOUND TARGET CHARACTER:', {
              charIndex,
              charBox,
              lineIndex,
              runIndex,
              charInRun,
              runText: run.text,
              charBoxLength: run.charBoxes.length
            });
            
            try {
              const [charX, charY, charWidth, charHeight] = charBox;
            
            // Position caret BETWEEN characters, not on top of them
            // For character at index N, position caret AFTER character N
            const caretX = charX + charWidth;
            
            console.log('[SimplifiedCaretSystem] Found character position:', {
              charIndex,
              charBox: [charX, charY, charWidth, charHeight],
              caretX: caretX,
              lineIndex,
              runIndex,
              charInRun
            });
            
            return {
              index: charIndex,
              coordinates: { 
                x: caretX, 
                y: charY, 
                space: 'CANVAS' as any, 
                precision: 'subpixel',
                timestamp: performance.now()
              },
              lineIndex,
              runIndex,
              charInRun,
              context: {
                isWordStart: false,
                isWordEnd: false,
                isLineStart: charInRun === 0,
                isLineEnd: charInRun === run.charBoxes.length - 1
              },
              metadata: {
                method: 'exact',
                confidence: 1.0,
                timing: { totalTime: 0 },
                debugInfo: { fallbackReason: 'initial_position' }
              }
            };
            } catch (error) {
              console.log('[SimplifiedCaretSystem] Failed to destructure charBox:', { charBox, error });
              continue;
            }
          }
          
          currentIndex++;
        }
      }
    }
    
    console.log('[SimplifiedCaretSystem] Character not found in layout plan:', {
      charIndex,
      finalCurrentIndex: currentIndex,
      totalCharacters: currentIndex,
      isAfterLastChar: charIndex === currentIndex,
      totalCharactersInLayout: totalCharacters,
      isIndexOutOfBounds: charIndex > totalCharacters,
      layoutPlanStructure: {
        linesCount: lines.length,
        totalCharacters,
        firstLineRuns: lines[0]?.runs?.length || 0,
        firstRunText: lines[0]?.runs?.[0]?.text || 'no text'
      }
    });
    
    // Check if we're trying to position AFTER the last character
    if (charIndex === currentIndex) {
      // Position caret AFTER the last character
      const lastLine = lines[lines.length - 1];
      const lastRun = lastLine.runs[lastLine.runs.length - 1];
      const lastCharBox = lastRun.charBoxes[lastRun.charBoxes.length - 1];
      
      try {
        const [lastCharX, lastCharY, lastCharWidth, lastCharHeight] = lastCharBox;
      
      // Position caret at the END of the last character
      const caretX = lastCharX + lastCharWidth;
      
      console.log('[SimplifiedCaretSystem] Found end-of-text position:', {
        charIndex,
        lastCharBox: [lastCharX, lastCharY, lastCharWidth, lastCharHeight],
        caretX: caretX,
        totalCharacters: currentIndex
      });
      
      return {
        index: charIndex,
        coordinates: { 
          x: caretX, 
          y: lastCharY, 
          space: 'CANVAS' as any, 
          precision: 'subpixel',
          timestamp: performance.now()
        },
        lineIndex: lines.length - 1,
        runIndex: lastLine.runs.length - 1,
        charInRun: lastRun.charBoxes.length,
        context: {
          isWordStart: false,
          isWordEnd: true,
          isLineStart: false,
          isLineEnd: true
        },
        metadata: {
          method: 'exact',
          confidence: 1.0,
          timing: { totalTime: 0 },
          debugInfo: { fallbackReason: 'end_of_text_position' }
        }
      };
      } catch (error) {
        console.log('[SimplifiedCaretSystem] Failed to destructure lastCharBox:', { lastCharBox, error });
        return null;
      }
    }
    
    console.log('[SimplifiedCaretSystem] Character position not found:', {
      charIndex,
      totalCharactersProcessed: currentIndex,
      linesProcessed: lines.length
    });
    
    return null;
  }

  // ============================================================================
  // MAIN POSITIONING API
  // ============================================================================
  
  /**
   * Position caret from mouse click - MAIN API
   */
  async positionFromClick(x: number, y: number): Promise<CaretPosition> {
    if (!this.isInitialized) {
      console.warn('[SimplifiedCaretSystem] System not initialized, using fallback');
      return this.getFallbackPosition(x, y);
    }
    
    const endTiming = this.performanceMonitor.startOperation('clickPositioning');
    
    try {
      // Check cache first for performance
      const cacheKey = this.generateCacheKey(x, y);
      if (this.positionCache.has(cacheKey)) {
        this.cacheHits++;
        const cachedPosition = this.positionCache.get(cacheKey)!;
        const hitRate = this.cacheHits / (this.cacheHits + this.cacheMisses) * 100;
        console.log('[SimplifiedCaretSystem] Cache hit for position:', {
          cacheKey,
          hitRate: `${hitRate.toFixed(1)}%`,
          totalHits: this.cacheHits,
          totalMisses: this.cacheMisses
        });
        return cachedPosition;
      }
      
      this.cacheMisses++;
      
      // Use precision hit testing for accurate positioning
      const hitResult = this.hitTestingEngine.hitTest(x, y);
      
      if (hitResult.confidence > 0.8) {
        this.currentPosition = hitResult.position;
        this.isVisible = true;
        
        // Cache high-confidence results
        this.cachePosition(cacheKey, hitResult.position);
        
        console.log('[SimplifiedCaretSystem] High-confidence positioning:', {
          clickedAt: { x, y },
          characterIndex: hitResult.position.index,
          confidence: hitResult.confidence
        });
        
        return hitResult.position;
      }
      
      // Fallback for low confidence results
      console.warn('[SimplifiedCaretSystem] Low confidence hit test, using improved fallback');
      const fallbackPosition = this.getImprovedFallbackPosition(x, y);
      this.cachePosition(cacheKey, fallbackPosition);
      return fallbackPosition;
      
    } finally {
      endTiming();
    }
  }
  
  /**
   * Navigate caret with keyboard - MAIN API
   */
  async navigateKeyboard(direction: string, unit: string = 'character'): Promise<CaretPosition> {
    if (!this.isInitialized || !this.currentPosition) {
      console.warn('[SimplifiedCaretSystem] System not initialized or no current position');
      return this.getFallbackPosition(0, 0);
    }
    
    const endTiming = this.performanceMonitor.startOperation('keyboardNavigation');
    
    try {
      const navigation = {
        direction: direction as any,
        unit: unit as any,
        withSelection: false
      };
      
      const newPosition = await this.caretManager.navigateWithKeyboard(navigation);
      
      if (newPosition) {
        this.currentPosition = newPosition;
        this.isVisible = true;
        
        console.log('[SimplifiedCaretSystem] Keyboard navigation:', {
          direction,
          unit,
          fromIndex: this.currentPosition.index,
          toIndex: newPosition.index
        });
        
        return newPosition;
      }
      
      return this.currentPosition;
      
    } finally {
      endTiming();
    }
  }
  
  // ============================================================================
  // RENDERING API
  // ============================================================================
  
  /**
   * Render caret - MAIN API
   */
  renderCaret(ctx: CanvasRenderingContext2D): void {
    if (this.config.debugMode) {
      console.log('[SimplifiedCaretSystem] renderCaret called:', {
        isVisible: this.isVisible,
        hasCurrentPosition: !!this.currentPosition,
        hasRenderingEngine: !!this.renderingEngine,
        currentPositionIndex: this.currentPosition?.index,
        isInitialized: this.isInitialized
      });
    }
    
    if (!this.isVisible || !this.currentPosition || !this.renderingEngine) {
      if (this.config.debugMode) {
        console.log('[SimplifiedCaretSystem] renderCaret skipped:', {
          reason: !this.isVisible ? 'not visible' : !this.currentPosition ? 'no position' : 'no rendering engine'
        });
      }
      return;
    }

    // Don't render if system isn't properly initialized (layout plan not ready)
    if (!this.isInitialized) {
      if (this.config.debugMode) {
        console.log('[SimplifiedCaretSystem] renderCaret skipped - system not initialized');
      }
      return;
    }

    // Don't render fallback positions at (0,0) unless we have a last known position
    if (this.currentPosition.coordinates.x === 0 && this.currentPosition.coordinates.y === 0 && !this.lastKnownPosition) {
      if (this.config.debugMode) {
        console.log('[SimplifiedCaretSystem] renderCaret skipped - waiting for layout plan');
      }
      return;
    }
    
    const endTiming = this.performanceMonitor.startOperation('caretRendering');
    
    try {
      // Determine context for styling
      const context = this.determineCaretContext(this.currentPosition);
      
      // Check if blinking system says we should show the caret
      const shouldShow = this.config.enableBlinking ? this.blinkingSystem.shouldShowCaret() : this.isVisible;
      
      const caretState: CaretState = {
        position: this.currentPosition,
        isVisible: shouldShow,
        isBlinking: this.config.enableBlinking,
        lastUpdateTime: performance.now(),
        animationState: 'idle',
        context: context
      };
      
      if (this.config.debugMode) {
      console.log('[SimplifiedCaretSystem] Rendering caret:', {
        position: this.currentPosition.coordinates,
        context: context
      });
    }

    // Use the context passed from PaperCanvas instead of the engine's own context
      this.renderingEngine.renderWithContext(caretState, ctx);
      
    } catch (error) {
      console.error('[SimplifiedCaretSystem] Rendering error:', error);
    } finally {
      endTiming();
    }
  }
  
  /**
   * Set caret visibility
   */
  setVisible(visible: boolean): void {
    this.isVisible = visible;
    if (this.config.debugMode) {
      console.log('[SimplifiedCaretSystem] Caret visibility set to:', visible);
    }
  }

  /**
   * Get the current layout plan from the hit testing engine
   */
  getCurrentLayoutPlan(): any {
    return this.hitTestingEngine?.getCurrentLayoutPlan();
  }

  /**
   * Calculate total character count in layout plan
   */
  private calculateLayoutCharacterCount(layoutPlan: any): number {
    let totalCharacters = 0;
    
    // Check for lines at different possible locations
    let lines = null;
    
    // NOTE: Backend only creates nested pages[0].frames[0].lines structure
    // Top-level lines structure is not created by backend TypesetterService
    // if (layoutPlan?.lines) {
    //   lines = layoutPlan.lines;
    // } else 
    
    if (layoutPlan?.pages?.[0]?.frames?.[0]?.lines) {
      lines = layoutPlan.pages[0].frames[0].lines;
    }
    
    if (!lines || !Array.isArray(lines)) {
      return 0;
    }
    
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      const line = lines[lineIndex];
      for (let runIndex = 0; runIndex < (line.runs || []).length; runIndex++) {
        const run = line.runs[runIndex];
        if (run?.charBoxes) {
          totalCharacters += run.charBoxes.length;
        }
      }
    }
    
    return totalCharacters;
  }

  /**
   * Get fallback position for character index that exceeds layout plan
   */
  private getFallbackPositionForIndex(charIndex: number, layoutPlan: any): CaretPosition | null {
    try {
      // Get the last valid position from the layout plan
      const layoutCharacterCount = this.calculateLayoutCharacterCount(layoutPlan);
      
      if (layoutCharacterCount === 0) {
        // No characters in layout plan, use document start
        const firstFrame = layoutPlan?.pages?.[0]?.frames?.[0];
        const contentX = firstFrame?.x || 24; // Use frame x or default margin
        const contentY = firstFrame?.y || 24; // Use frame y or default margin
        
        return {
          index: charIndex,
          coordinates: { 
            x: contentX, 
            y: contentY, 
            space: CoordinateSpace.CANVAS,
            precision: 'pixel' as const,
            timestamp: performance.now() 
          },
          lineIndex: 0,
          charInRun: 0,
          runIndex: 0,
          context: {
            isWordStart: true,
            isLineStart: true,
            isLineEnd: false,
            isParagraphStart: true,
            isParagraphEnd: false,
            isInCitation: false,
            isInClause: false,
            isInHeader: false,
            isInFooter: false,
            isRTL: false
          },
          metadata: {
            method: 'fallback',
            confidence: 0.5,
            timing: { totalTime: 0 },
            debugInfo: { fallbackReason: 'No characters in layout plan' }
          }
        };
      }
      
      // Find the last character position in the layout plan
      const lastPosition = this.findCharacterPositionFast(layoutCharacterCount - 1, layoutPlan);
      if (lastPosition) {
        // Extend from the last valid position with intelligent character width estimation
        let charWidth = 8; // Default fallback
        
        // Try to get actual character width from layout plan
        try {
          const firstRun = layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0];
          if (firstRun?.glyphAdvances && firstRun.glyphAdvances.length > 0) {
            // Use average glyph advance from the layout plan
            const totalAdvance = firstRun.glyphAdvances.reduce((sum: number, advance: number) => sum + advance, 0);
            charWidth = totalAdvance / firstRun.glyphAdvances.length;
          } else if (firstRun?.fontSize) {
            // Fallback to font-size based estimation
            charWidth = firstRun.fontSize * 0.6;
          }
        } catch (error) {
          console.log('[SimplifiedCaretSystem] Could not extract character width from layout plan, using default');
        }
        
        return {
          index: charIndex,
          coordinates: { 
            x: lastPosition.coordinates.x + (charWidth * (charIndex - layoutCharacterCount + 1)), 
            y: lastPosition.coordinates.y, 
            space: CoordinateSpace.CANVAS,
            precision: 'pixel' as const,
            timestamp: performance.now() 
          },
          lineIndex: lastPosition.lineIndex,
          charInRun: 0,
          runIndex: 0,
          context: {
            isWordEnd: true,
            isLineStart: false,
            isLineEnd: true,
            isParagraphStart: false,
            isParagraphEnd: true,
            isInCitation: false,
            isInClause: false,
            isInHeader: false,
            isInFooter: false,
            isRTL: false
          },
          metadata: {
            method: 'fallback',
            confidence: 0.7,
            timing: { totalTime: 0 },
            debugInfo: { 
              fallbackReason: 'Character index exceeds layout plan',
              contextAnalysis: {
                layoutCharacterCount,
                targetCharIndex: charIndex,
                extendedFrom: layoutCharacterCount - 1
              }
            }
          }
        };
      }
      
      return null;
    } catch (error) {
      console.warn('[SimplifiedCaretSystem] Failed to create fallback position:', error);
      return null;
    }
  }

  /**
   * Update caret position from character index with two-phase positioning
   */
  async updateCaretPosition(charIndex: number, layoutPlan?: any, options?: CaretPositioningOptions): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[SimplifiedCaretSystem] System not initialized, cannot update caret position');
      return;
    }

    // PHASE 1: Immediate positioning using frontend text (if available)
    if (options?.useFrontendText && options.frontendText && options.fontMetrics && options.documentMetrics) {
      const immediatePosition = this.frontendPositionCalculator.calculatePosition(
        options.frontendText,
        charIndex,
        options.fontMetrics,
        options.documentMetrics
      );
      
      if (immediatePosition) {
        this.currentPosition = immediatePosition;
        this.isVisible = true;
        this.lastKnownPosition = immediatePosition;
        
        // Update blinking system position if enabled
        if (this.config.enableBlinking) {
          this.blinkingSystem.updatePosition({
            x: immediatePosition.coordinates.x,
            y: immediatePosition.coordinates.y,
            height: options.fontMetrics.fontSize
          });
        }
        
        console.log('[SimplifiedCaretSystem] Phase 1 - Immediate positioning:', {
          characterIndex: charIndex,
          coordinates: immediatePosition.coordinates,
          method: 'frontend-immediate',
          confidence: immediatePosition.metadata?.confidence || 0.8
        });
        
        // Continue to Phase 2 for backend refinement (if layout plan available)
        // This allows immediate response while backend catches up
      }
    }

    try {
      // Use current layout plan from hit testing engine (most up-to-date)
      const currentLayoutPlan = this.hitTestingEngine?.getCurrentLayoutPlan();
      const actualLayoutPlan = currentLayoutPlan?.data?.layoutPlan || currentLayoutPlan || layoutPlan?.data?.layoutPlan || layoutPlan;
      
      // CRITICAL DEBUG: Layout plan availability check
      console.log('[SimplifiedCaretSystem] Layout plan availability check:', {
        hasHitTestingEngine: !!this.hitTestingEngine,
        hasCurrentLayoutPlan: !!currentLayoutPlan,
        currentLayoutPlanType: typeof currentLayoutPlan,
        hasLayoutPlanParam: !!layoutPlan,
        layoutPlanParamType: typeof layoutPlan,
        hasActualLayoutPlan: !!actualLayoutPlan,
        actualLayoutPlanType: typeof actualLayoutPlan,
        actualLayoutPlanKeys: actualLayoutPlan ? Object.keys(actualLayoutPlan) : [],
        timestamp: performance.now()
      });
      
      // DEBUG: Comprehensive debugging for character position lookup
      console.log('[SimplifiedCaretSystem] updateCaretPosition DEBUG:', {
        charIndex,
        hasHitTestingEngine: !!this.hitTestingEngine,
        hasCurrentLayoutPlan: !!currentLayoutPlan,
        currentLayoutPlanType: typeof currentLayoutPlan,
        hasLayoutPlanParam: !!layoutPlan,
        layoutPlanParamType: typeof layoutPlan,
        hasActualLayoutPlan: !!actualLayoutPlan,
        actualLayoutPlanType: typeof actualLayoutPlan,
        timestamp: performance.now()
      });
      
      if (currentLayoutPlan) {
        console.log('[SimplifiedCaretSystem] Current layout plan structure:', {
          hasData: !!currentLayoutPlan.data,
          hasLayoutPlan: !!currentLayoutPlan.data?.layoutPlan,
          hasPages: !!currentLayoutPlan.pages,
          hasPageSetup: !!currentLayoutPlan.pageSetup,
          hasPlanHash: !!currentLayoutPlan.planHash,
          planHashValue: currentLayoutPlan.planHash,
          keys: Object.keys(currentLayoutPlan)
        });
      }
      
      if (actualLayoutPlan) {
        console.log('[SimplifiedCaretSystem] Actual layout plan structure:', {
          hasPages: !!actualLayoutPlan.pages,
          pageCount: actualLayoutPlan.pages?.length || 0,
          hasPageSetup: !!actualLayoutPlan.pageSetup,
          hasPlanHash: !!actualLayoutPlan.planHash,
          planHashValue: actualLayoutPlan.planHash,
          keys: Object.keys(actualLayoutPlan)
        });
        
        if (actualLayoutPlan.pages?.[0]) {
          const firstPage = actualLayoutPlan.pages[0];
          console.log('[SimplifiedCaretSystem] First page structure:', {
            hasFrames: !!firstPage.frames,
            frameCount: firstPage.frames?.length || 0,
            hasLines: !!firstPage.frames?.[0]?.lines,
            lineCount: firstPage.frames?.[0]?.lines?.length || 0,
            hasRuns: !!firstPage.frames?.[0]?.lines?.[0]?.runs,
            runCount: firstPage.frames?.[0]?.lines?.[0]?.runs?.length || 0
          });
        }
      }
      
      
      // Check if we have a valid layout plan with the expected structure
      // Support both top-level lines and nested pages[0].frames[0].lines structure
      const hasValidStructure = (actualLayoutPlan && actualLayoutPlan.lines) || 
                                (actualLayoutPlan && actualLayoutPlan.pages && actualLayoutPlan.pages[0] && actualLayoutPlan.pages[0].frames && actualLayoutPlan.pages[0].frames[0]);
      
      if (hasValidStructure) {
        // IMPROVED: More lenient validation - only reject if significantly out of bounds
        const layoutCharacterCount = this.calculateLayoutCharacterCount(actualLayoutPlan);
        
        // Allow small differences (up to 10 characters) due to timing lag
        const maxAllowedDifference = 10;
        if (charIndex > layoutCharacterCount + maxAllowedDifference) {
          console.warn(`[SimplifiedCaretSystem] Character index ${charIndex} significantly exceeds layout plan character count ${layoutCharacterCount}. Using fallback positioning.`);
          
          // Use fallback positioning until layout plan is updated
          const fallbackPosition = this.getFallbackPositionForIndex(charIndex, actualLayoutPlan);
          if (fallbackPosition) {
            this.currentPosition = fallbackPosition;
            this.isVisible = true;
            this.lastKnownPosition = fallbackPosition;
            
            console.log('[SimplifiedCaretSystem] Using fallback position:', {
              characterIndex: fallbackPosition.index,
              coordinates: fallbackPosition.coordinates,
              method: 'fallback-due-to-index-mismatch'
            });
            return;
          }
        } else if (charIndex > layoutCharacterCount) {
          // Character index exceeds layout plan - use intelligent fallback
          console.log(`[SimplifiedCaretSystem] Character index ${charIndex} exceeds layout plan character count ${layoutCharacterCount}. Using intelligent fallback positioning.`);
          
          const fallbackPosition = this.getFallbackPositionForIndex(charIndex, actualLayoutPlan);
          if (fallbackPosition) {
            this.currentPosition = fallbackPosition;
            this.isVisible = true;
            this.lastKnownPosition = fallbackPosition;
            
            console.log('[SimplifiedCaretSystem] Using intelligent fallback position:', {
              characterIndex: fallbackPosition.index,
              coordinates: fallbackPosition.coordinates,
              method: 'intelligent-fallback'
            });
            return;
          }
        }
        
        const position = this.findCharacterPositionFast(charIndex, actualLayoutPlan);
        
        if (position) {
          // PHASE 2: Backend refinement - only update if more accurate than frontend position
          const shouldRefine = !this.currentPosition || 
                               (this.currentPosition.metadata?.method as string) === 'frontend-immediate' ||
                               (position.metadata?.confidence || 0) > (this.currentPosition.metadata?.confidence || 0);
          
          if (shouldRefine) {
            this.currentPosition = position;
            this.isVisible = true;
            this.lastKnownPosition = position;
            
            // Update blinking system position if enabled
            if (this.config.enableBlinking) {
              this.blinkingSystem.updatePosition({
                x: position.coordinates.x,
                y: position.coordinates.y,
                height: this.extractFontSizeFromLayoutPlan() || 20
              });
            }
            
            console.log('[SimplifiedCaretSystem] Phase 2 - Backend refinement:', {
              characterIndex: charIndex,
              coordinates: position.coordinates,
              method: 'backend-refined',
              confidence: position.metadata?.confidence || 1.0,
              refined: true
            });
          } else {
            console.log('[SimplifiedCaretSystem] Phase 2 - Backend refinement skipped:', {
              characterIndex: charIndex,
              reason: 'frontend_position_more_accurate',
              frontendConfidence: this.currentPosition?.metadata?.confidence || 0,
              backendConfidence: position.metadata?.confidence || 0
            });
          }
        } else {
          console.warn('[SimplifiedCaretSystem] Could not find position for character index:', charIndex);
          // Use intelligent fallback instead of generic fallback
          const fallbackPosition = this.getFallbackPositionForIndex(charIndex, actualLayoutPlan);
          if (fallbackPosition) {
            this.currentPosition = fallbackPosition;
            this.isVisible = true;
            this.lastKnownPosition = fallbackPosition;
            
            console.log('[SimplifiedCaretSystem] Using fallback position for missing character:', {
              characterIndex: fallbackPosition.index,
              coordinates: fallbackPosition.coordinates,
              method: 'fallback-for-missing-character'
            });
          } else {
            // Last resort: generic fallback
            this.currentPosition = this.getFallbackPosition(0, 0);
            this.currentPosition.index = charIndex;
            this.isVisible = true;
          }
        }
      } else {
        console.warn('[SimplifiedCaretSystem] No layout plan available for position update');
        // Set fallback position
        this.currentPosition = this.getFallbackPosition(0, 0);
        this.currentPosition.index = charIndex;
        this.isVisible = true;
      }
    } catch (error) {
      console.error('[SimplifiedCaretSystem] Failed to update caret position:', error);
      // Set fallback position
      this.currentPosition = this.getFallbackPosition(0, 0);
      this.currentPosition.index = charIndex;
      this.isVisible = true;
    }
  }
  
  // ============================================================================
  // SYSTEM MANAGEMENT
  // ============================================================================
  
  /**
   * Update layout plan when backend changes (enhanced with smart change detection)
   */
  updateLayoutPlan(layoutPlan: any, changeMetadata?: ChangeMetadata): void {
    if (!layoutPlan) {
      console.warn('[SimplifiedCaretSystem] No layout plan provided for update');
      return;
    }
    
    // 🔧 SMART CHANGE DETECTION: Log change metadata for debugging
    if (changeMetadata) {
      console.log('[SimplifiedCaretSystem] Layout update with change metadata:', {
        changeType: changeMetadata.changeType,
        affectsKerning: changeMetadata.affectsKerning,
        affectsLigatures: changeMetadata.affectsLigatures,
        affectsMargins: changeMetadata.affectsMargins,
        requiresLayoutUpdate: changeMetadata.requiresLayoutUpdate
      });
    }
    
    console.log('[SimplifiedCaretSystem] Updating layout plan...');
    
    // Handle nested structure: data.layoutPlan
    const actualLayoutPlan = layoutPlan?.data?.layoutPlan || layoutPlan;
    
    // CRITICAL: If system is not initialized and we have a valid layout plan, initialize now
    if (!this.isInitialized && this.isValidLayoutPlan(layoutPlan)) {
      console.log('[SimplifiedCaretSystem] Layout plan arrived, completing deferred initialization');
      const pendingCanvas = this.pendingInitialization?.canvas;
      const pendingCaretPosition = this.pendingInitialization?.initialCaretPosition;
      
      this.initializeWithLayoutPlan(layoutPlan, pendingCaretPosition)
        .then(() => {
          this.isInitialized = true;
          this.pendingInitialization = null;
          console.log('[SimplifiedCaretSystem] Deferred initialization completed successfully');
          
          // Initialize blinking system if enabled and we have a position
          if (this.config.enableBlinking && this.currentPosition && pendingCanvas) {
            this.blinkingSystem.initialize(pendingCanvas, {
              x: this.currentPosition.coordinates.x,
              y: this.currentPosition.coordinates.y,
              height: this.extractFontSizeFromLayoutPlan() || 20
            });
          }
        })
        .catch((error) => {
          console.error('[SimplifiedCaretSystem] Deferred initialization failed:', error);
        });
      return;
    }
    
    // Normal layout plan update for already initialized system
    try {
      // 🔧 SMART CHANGE DETECTION: Optimize caret updates based on change type
      const shouldUpdateCaretPosition = this.shouldUpdateCaretPosition(changeMetadata);
      
      if (shouldUpdateCaretPosition) {
        // Clear cache when layout changes affect caret positioning
        this.clearCache();
        
        const planHash = this.generateLayoutPlanHash(actualLayoutPlan);
        this.hitTestingEngine.updateLayoutPlan(actualLayoutPlan, planHash);
        
        // Update current caret position if it exists
        if (this.currentPosition) {
          this.updateCurrentCaretPosition(actualLayoutPlan);
        }
        
        console.log('[SimplifiedCaretSystem] Layout plan updated with caret repositioning:', planHash.slice(0, 8));
      } else {
        // Just update the layout plan without clearing cache or repositioning caret
        const planHash = this.generateLayoutPlanHash(actualLayoutPlan);
        this.hitTestingEngine.updateLayoutPlan(actualLayoutPlan, planHash);
        
        console.log('[SimplifiedCaretSystem] Layout plan updated (caret position preserved):', planHash.slice(0, 8));
      }
      
    } catch (error) {
      console.error('[SimplifiedCaretSystem] Layout plan update failed:', error);
    }
  }
  
  /**
   * 🔧 SMART CHANGE DETECTION: Determine if caret position should be updated
   */
  private shouldUpdateCaretPosition(changeMetadata?: ChangeMetadata): boolean {
    // If no change metadata, always update (fallback to old behavior)
    if (!changeMetadata) {
      return true;
    }
    
    // Always update for formatting changes, paste operations, or navigation
    if (changeMetadata.changeType === 'formatting' || 
        changeMetadata.changeType === 'paste' || 
        changeMetadata.changeType === 'navigation') {
      return true;
    }
    
    // Update if the change affects margins (line breaks, pagination)
    if (changeMetadata.affectsMargins) {
      return true;
    }
    
    // For insert/delete operations, only update if they affect kerning or ligatures
    // Simple character insertions/deletions that don't affect shaping can preserve caret position
    if (changeMetadata.affectsKerning || changeMetadata.affectsLigatures) {
      return true;
    }
    
    // For simple text changes that don't affect shaping, preserve caret position
    return false;
  }
  
  /**
   * 🔧 CARET SYNC FIX: Update current caret position efficiently
   * Instead of trying to maintain old character index, recalculate based on current text
   */
  private updateCurrentCaretPosition(layoutPlan: any): void {
    if (!this.currentPosition) {
      return;
    }
    
    // ✅ CARET SYNC FIX: Get current text length from layout plan
    const currentTextLength = this.extractTextLength(layoutPlan);
    
    // ✅ CARET SYNC FIX: Clamp caret position to valid range
    const currentIndex = Math.min(this.currentPosition.index, Math.max(0, currentTextLength));
    
    console.log('🔧 [CARET_SYNC_FIX] Updating caret position:', {
      oldIndex: this.currentPosition.index,
      newIndex: currentIndex,
      textLength: currentTextLength,
      wasClamped: currentIndex !== this.currentPosition.index
    });
    
    const newPosition = this.findCharacterPositionFast(currentIndex, layoutPlan);
    
    if (newPosition) {
      this.currentPosition = newPosition;
      
      // Update blinking system position if enabled
      if (this.config.enableBlinking && this.blinkingSystem) {
        this.blinkingSystem.updatePosition({
          x: newPosition.coordinates.x,
          y: newPosition.coordinates.y,
          height: this.extractFontSizeFromLayoutPlan() || 20
        });
      }
      
      console.log('🔧 [CARET_SYNC_FIX] Caret position updated:', {
        index: newPosition.index,
        coordinates: newPosition.coordinates
      });
    } else {
      console.warn('🔧 [CARET_SYNC_FIX] Failed to find new caret position, keeping current');
    }
  }
  
  /**
   * Get current position
   */
  getCurrentPosition(): CaretPosition | null {
    return this.currentPosition;
  }
  
  /**
   * 🔧 SMART CHANGE DETECTION: Handle text changes with smart optimization
   */
  handleTextChange(newText: string, changeMetadata?: ChangeMetadata): void {
    if (!this.isInitialized) {
      console.warn('[SimplifiedCaretSystem] System not initialized, ignoring text change');
      return;
    }
    
    // Update internal text tracking
    this.textLength = newText.length;
    
    // ✅ CARET SYNC FIX: Clamp current caret position to valid range
    if (this.currentPosition) {
      const clampedIndex = Math.min(this.currentPosition.index, Math.max(0, newText.length));
      
      if (clampedIndex !== this.currentPosition.index) {
        console.log('🔧 [CARET_SYNC_FIX] Clamping caret position during text change:', {
          oldIndex: this.currentPosition.index,
          newIndex: clampedIndex,
          textLength: newText.length
        });
        
        this.currentPosition.index = clampedIndex;
      }
    }
    
    // Log change metadata for debugging
    if (changeMetadata) {
      console.log('[SimplifiedCaretSystem] Text change handled:', {
        textLength: newText.length,
        changeType: changeMetadata.changeType,
        requiresLayoutUpdate: changeMetadata.requiresLayoutUpdate,
        affectsKerning: changeMetadata.affectsKerning,
        affectsLigatures: changeMetadata.affectsLigatures,
        affectsMargins: changeMetadata.affectsMargins
      });
    }
    
    // For changes that don't require layout updates, just update text tracking
    if (changeMetadata && !changeMetadata.requiresLayoutUpdate) {
      console.log('[SimplifiedCaretSystem] Text change preserved - no layout update needed');
      return;
    }
    
    // For changes that do require layout updates, the layout plan will be updated separately
    // This method just handles the text change notification
    console.log('[SimplifiedCaretSystem] Text change processed - layout update expected');
  }
  
  /**
   * Get performance metrics
   */
  getPerformanceMetrics(): SimplifiedCaretMetrics {
    const baseMetrics = this.performanceMonitor.getMetrics();
    
    return {
      positioningTime: baseMetrics.hitTestTime,
      renderingTime: baseMetrics.renderingTime,
      cacheHitRate: this.cacheHits / (this.cacheHits + this.cacheMisses) || 0,
      totalOperations: baseMetrics.totalOperations,
      averageConfidence: 0.95 // Would be calculated from actual hit test results
    };
  }
  
  /**
   * Get system health status
   */
  getSystemHealth(): { status: string; details: any } {
    return {
      status: this.isInitialized ? 'healthy' : 'not_initialized',
      details: {
        initialized: this.isInitialized,
        hasRenderingEngine: !!this.renderingEngine,
        cacheSize: this.positionCache.size,
        cacheHitRate: this.getPerformanceMetrics().cacheHitRate,
        currentPosition: this.currentPosition?.index || null
      }
    };
  }
  
  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================
  
  private determineCaretContext(position: CaretPosition): CaretContext {
    // Analyze document structure around caret position
    // Extract font information from layout plan for proper scaling
    
    const fontSize = this.extractFontSizeFromLayoutPlan() || 20;
    const fontFamily = this.extractFontFamilyFromLayoutPlan() || 'Times New Roman';
    
    console.log('[SimplifiedCaretSystem] Caret context font scaling:', {
      extractedFontSize: fontSize,
      fontFamily: fontFamily,
      lineHeight: fontSize * 1.4,
      position: position.index
    });
    
    return {
      isInParagraph: true,
      isInCitation: false,
      isInClause: false,
      isInHeader: false,
      isInFooter: false,
      isAtWordBoundary: true,
      isAtLineStart: position.charInRun === 0,
      isAtLineEnd: false,
      isAtDocumentStart: position.index === 0,
      isAtDocumentEnd: false,
      fontFamily: fontFamily,
      fontSize: fontSize,
      lineHeight: fontSize * 1.4, // Scale line height with font size
      textDirection: 'ltr'
    };
  }
  
  private generateCacheKey(x: number, y: number): string {
    // Hybrid approach: Use exact coordinates for accuracy
    // But round to reduce cache size while maintaining precision
    const precision = 1; // 1px precision (high accuracy)
    const roundedX = Math.round(x * precision) / precision;
    const roundedY = Math.round(y * precision) / precision;
    return `${roundedX}:${roundedY}`;
  }
  
  private generateLayoutPlanHash(layoutPlan: any): string {
    // Simple hash generation for layout plan identification
    const planString = JSON.stringify(layoutPlan || {});
    let hash = 0;
    for (let i = 0; i < planString.length; i++) {
      const char = planString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
  }

  /**
   * Extract total text length from layout plan for performance optimization
   */
  private extractTextLength(layoutPlan: any): number {
    if (!layoutPlan?.pages?.[0]?.frames?.[0]?.lines) {
      return 0;
    }

    let totalLength = 0;
    const lines = layoutPlan.pages[0].frames[0].lines;

    // Check if lines is iterable
    if (!Array.isArray(lines)) {
      console.log('[SimplifiedCaretSystem] Lines is not an array in extractTextLength:', { lines, type: typeof lines });
      return 0;
    }

    for (const line of lines) {
      for (const run of line.runs || []) {
        if (run?.text) {
          totalLength += run.text.length;
        }
      }
    }

    console.log('[SimplifiedCaretSystem] Extracted text length:', totalLength);
    return totalLength;
  }

  /**
   * Extract font size from layout plan for proper caret scaling
   */
  private extractFontSizeFromLayoutPlan(): number | null {
    if (!this.hitTestingEngine) return null;
    
    try {
      // Get the current layout plan from hit testing engine
      const layoutPlan = this.hitTestingEngine.getCurrentLayoutPlan();
      if (!layoutPlan) return null;
      
      // Extract font size from layout plan - use fontSizePx for accurate rendering
      const fontSizePx = layoutPlan?.fontSizePx;
      if (fontSizePx) {
        console.log('[SimplifiedCaretSystem] Extracted font size (fontSizePx):', fontSizePx);
        return fontSizePx;
      }
      
      // Fallback to first run fontSize
      const firstRun = layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0];
      if (firstRun?.fontSize) {
        console.log('[SimplifiedCaretSystem] Extracted font size (fallback):', firstRun.fontSize);
        return firstRun.fontSize;
      }
      
      return null;
    } catch (error) {
      console.warn('[SimplifiedCaretSystem] Failed to extract font size:', error);
      return null;
    }
  }

  /**
   * Extract font family from layout plan
   */
  private extractFontFamilyFromLayoutPlan(): string | null {
    if (!this.hitTestingEngine) return null;
    
    try {
      // Get the current layout plan from hit testing engine
      const layoutPlan = this.hitTestingEngine.getCurrentLayoutPlan();
      if (!layoutPlan) return null;
      
      // Extract font family from first run
      const firstRun = layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0];
      if (firstRun?.fontFamily) {
        console.log('[SimplifiedCaretSystem] Extracted font family:', firstRun.fontFamily);
        return firstRun.fontFamily;
      }
      
      return null;
    } catch (error) {
      console.warn('[SimplifiedCaretSystem] Failed to extract font family:', error);
      return null;
    }
  }
  
  private cachePosition(key: string, position: CaretPosition): void {
    // Manage cache size
    if (this.positionCache.size >= this.maxCacheSize) {
      const firstKey = this.positionCache.keys().next().value;
      if (firstKey) {
        this.positionCache.delete(firstKey);
      }
    }
    
    this.positionCache.set(key, position);
  }
  
  private clearCache(): void {
    this.positionCache.clear();
    this.cacheHits = 0;
    this.cacheMisses = 0;
    console.log('[SimplifiedCaretSystem] Position cache cleared');
  }
  
  /**
   * Improved fallback positioning using layout plan data
   */
  private getImprovedFallbackPosition(x: number, y: number): CaretPosition {
    // Try to use layout plan data for better accuracy
    if (this.hitTestingEngine) {
      try {
        const layoutPlan = this.hitTestingEngine.getCurrentLayoutPlan();
        console.log('[SimplifiedCaretSystem] getImprovedFallbackPosition debug:', {
          hasHitTestingEngine: !!this.hitTestingEngine,
          hasLayoutPlan: !!layoutPlan,
          layoutPlanType: typeof layoutPlan,
          hasPages: !!layoutPlan?.pages,
          pagesLength: layoutPlan?.pages?.length || 0
        });
        
        // NOTE: Backend only creates nested pages[0].frames[0].lines structure
        // Top-level lines structure is not created by backend TypesetterService
        let lines = null;
        // if (layoutPlan?.lines) {
        //   lines = layoutPlan.lines;
        // } else 
        
        if (layoutPlan?.pages?.[0]?.frames?.[0]?.lines) {
          lines = layoutPlan.pages[0].frames[0].lines;
        }
        
        if (lines) {
          
          // Check if lines is iterable
          if (!Array.isArray(lines)) {
            console.log('[SimplifiedCaretSystem] Lines is not an array in getImprovedFallbackPosition:', { lines, type: typeof lines });
            return this.getFallbackPosition(x, y);
          }
          
          // Find the closest line based on Y coordinate
          let closestLine = lines[0];
          let minDistance = Math.abs(y - closestLine.y);
          
          for (const line of lines) {
            const distance = Math.abs(y - line.y);
            if (distance < minDistance) {
              minDistance = distance;
              closestLine = line;
            }
          }
          
          // Check if click is beyond the end of text (end-of-text positioning)
          if (closestLine.runs?.[0]?.charBoxes) {
            const charBoxes = closestLine.runs[0].charBoxes;
            const lastCharBox = charBoxes[charBoxes.length - 1];
            
            try {
              const [lastCharX, lastCharY, lastCharWidth, lastCharHeight] = lastCharBox;
            const textEndX = lastCharX + lastCharWidth;
            
            // If click is beyond the end of text, position at end
            console.log('[SimplifiedCaretSystem] Checking end-of-text:', {
              clickedAt: { x, y },
              textEndX: textEndX,
              isBeyondText: x > textEndX,
              lastCharBox: [lastCharX, lastCharY, lastCharWidth, lastCharHeight]
            });
            
            if (x > textEndX) {
              console.log('[SimplifiedCaretSystem] End-of-text positioning:', {
                clickedAt: { x, y },
                textEndX: textEndX,
                caretPosition: { x: textEndX, y: lastCharY }
              });
              
              return {
                index: charBoxes.length, // After last character
                coordinates: { 
                  x: textEndX, 
                  y: lastCharY, 
                  space: 'CANVAS' as any, 
                  precision: 'pixel',
                  timestamp: performance.now()
                },
                lineIndex: 0,
                runIndex: 0,
                charInRun: charBoxes.length,
                context: {
                  isWordStart: false,
                  isWordEnd: true,
                  isLineStart: false,
                  isLineEnd: true
                },
                metadata: {
                  method: 'fallback',
                  confidence: 0.8, // High confidence for end-of-text
                  timing: { totalTime: 0 },
                  debugInfo: { fallbackReason: 'End-of-text positioning' }
                }
              };
            }
            } catch (error) {
              console.log('[SimplifiedCaretSystem] Failed to destructure lastCharBox in fallback:', { lastCharBox, error });
              return this.getFallbackPosition(x, y);
            }
            
            // Find the closest character in that line
            let closestCharIndex = 0;
            let minCharDistance = Math.abs(x - charBoxes[0][0]);
            
            for (let i = 0; i < charBoxes.length; i++) {
              try {
                const [charX, , charWidth] = charBoxes[i];
                const distance = Math.abs(x - charX);
                if (distance < minCharDistance) {
                  minCharDistance = distance;
                  closestCharIndex = i;
                }
              } catch (error) {
                console.log('[SimplifiedCaretSystem] Failed to destructure charBox in loop:', { charBox: charBoxes[i], index: i, error });
                continue;
              }
            }
            
            // Determine caret position based on click location (MS Word behavior)
            try {
              const [charX, charY, charWidth, charHeight] = charBoxes[closestCharIndex];
            
            // If clicking in the first half of the character, position before it
            // If clicking in the second half, position after it
            const caretX = (x < charX + charWidth / 2) ? charX : charX + charWidth;
            
            // Special case: If clicking at the very end of the last character, position after it
            if (closestCharIndex === charBoxes.length - 1 && x > charX + charWidth * 0.8) {
              const endCaretX = charX + charWidth;
              console.log('[SimplifiedCaretSystem] End-of-character positioning:', {
                clickedAt: { x, y },
                charIndex: closestCharIndex,
                charEndX: endCaretX,
                caretPosition: { x: endCaretX, y: charY }
              });
              
              return {
                index: charBoxes.length, // After last character
                coordinates: { 
                  x: endCaretX, 
                  y: charY, 
                  space: 'CANVAS' as any, 
                  precision: 'pixel',
                  timestamp: performance.now()
                },
                lineIndex: 0,
                runIndex: 0,
                charInRun: charBoxes.length,
                context: {
                  isWordStart: false,
                  isWordEnd: true,
                  isLineStart: false,
                  isLineEnd: true
                },
                metadata: {
                  method: 'fallback',
                  confidence: 0.7,
                  timing: { totalTime: 0 },
                  debugInfo: { fallbackReason: 'End-of-character positioning' }
                }
              };
            }
            
            console.log('[SimplifiedCaretSystem] Improved fallback positioning:', {
              clickedAt: { x, y },
              closestCharIndex,
              caretPosition: { x: caretX, y: charY }
            });
            
            return {
              index: closestCharIndex,
              coordinates: { 
                x: caretX, 
                y: charY, 
                space: 'CANVAS' as any, 
                precision: 'pixel',
                timestamp: performance.now()
              },
              lineIndex: 0,
              runIndex: 0,
              charInRun: closestCharIndex,
              context: {
                isWordStart: false,
                isWordEnd: false,
                isLineStart: closestCharIndex === 0,
                isLineEnd: closestCharIndex === charBoxes.length - 1
              },
              metadata: {
                method: 'fallback',
                confidence: 0.6, // Higher confidence for layout-based fallback
                timing: { totalTime: 0 },
                debugInfo: { fallbackReason: 'Improved layout-based positioning' }
              }
            };
            } catch (error) {
              console.log('[SimplifiedCaretSystem] Failed to destructure closestCharBox:', { charBox: charBoxes[closestCharIndex], index: closestCharIndex, error });
              return this.getFallbackPosition(x, y);
            }
          }
        }
      } catch (error) {
        console.warn('[SimplifiedCaretSystem] Improved fallback failed:', error);
      }
    }
    
    // Fall back to basic fallback if improved method fails
    return this.getFallbackPosition(x, y);
  }

  private getFallbackPosition(x: number, y: number): CaretPosition {
    // Smart fallback positioning using last known position
    // If coordinates are (0,0) and we have a last known position, use it
    if (x === 0 && y === 0 && this.lastKnownPosition) {
      console.log('[SimplifiedCaretSystem] Using last known position fallback:', {
        lastPosition: this.lastKnownPosition.coordinates,
        lastIndex: this.lastKnownPosition.index
      });
      
      // Return the last known position with updated timestamp
      return {
        ...this.lastKnownPosition,
        coordinates: {
          ...this.lastKnownPosition.coordinates,
          timestamp: performance.now()
        },
        metadata: {
          ...this.lastKnownPosition.metadata,
          method: 'fallback',
          confidence: 0.7, // Higher confidence since it's a real position
          timing: { totalTime: 0 },
          debugInfo: { fallbackReason: 'Layout plan not ready, using last known position' }
        }
      };
    }
    
    // Fallback to document margins only if no last known position
    let fallbackX = x;
    let fallbackY = y;
    
    if (x === 0 && y === 0) {
      fallbackX = 72; // Left margin
      fallbackY = 72; // Top margin
      console.log('[SimplifiedCaretSystem] Using document margin fallback (no last position):', { x: fallbackX, y: fallbackY });
    }
    
    // Get character width from current layout plan or cached positions
    const layoutPlan = this.hitTestingEngine?.getCurrentLayoutPlan();
    let charWidth = 6.2; // Fallback default
    let lineHeight = 19.2; // Fallback default
    let charsPerLine = 108; // Fallback default
    
    if (layoutPlan?.pages?.[0]?.frames?.[0]?.lines?.[0]?.runs?.[0]?.glyphAdvances) {
      // Use actual HarfBuzz character widths from layout plan
      const charWidths = layoutPlan.pages[0].frames[0].lines[0].runs[0].glyphAdvances;
      
      // Sanity check: ensure we have character widths
      if (charWidths.length > 0) {
        charWidth = charWidths.reduce((sum: number, w: number) => sum + w, 0) / charWidths.length;
        
        // Get actual line height from layout plan
        const line = layoutPlan.pages[0].frames[0].lines[0];
        lineHeight = line.height || 19.2;
        
        // Calculate characters per line based on actual content width
        const frame = layoutPlan.pages[0].frames[0];
        // Frame width is already the content width (margins already subtracted by backend)
        const contentWidth = frame.width;
        
        // Sanity check: prevent division by zero
        if (charWidth > 0) {
          charsPerLine = Math.floor(contentWidth / charWidth);
        }
        
        console.log('[SimplifiedCaretSystem] Using HarfBuzz measurements:', {
          charWidths,
          avgCharWidth: charWidth,
          lineHeight,
          contentWidth,
          charsPerLine
        });
      }
    } else if (this.characterIndexCache.size > 0) {
      // Fallback to cached character positions
      const cachedPositions = Array.from(this.characterIndexCache.values());
      if (cachedPositions.length >= 2) {
        const sortedPositions = cachedPositions.sort((a, b) => a.index - b.index);
        let totalCharWidth = 0;
        let charCount = 0;
        
        for (let i = 1; i < sortedPositions.length; i++) {
          const prev = sortedPositions[i - 1];
          const curr = sortedPositions[i];
          
          // Only use consecutive characters on same line
          if (curr.lineIndex === prev.lineIndex && curr.index === prev.index + 1) {
            const width = curr.coordinates.x - prev.coordinates.x;
            if (width > 0 && width < 50) { // Sanity check
              totalCharWidth += width;
              charCount++;
            }
          }
        }
        
        if (charCount > 0) {
          charWidth = totalCharWidth / charCount;
          
          // Try to get dynamic measurements from layout plan if available
          const layoutPlan = this.hitTestingEngine?.getCurrentLayoutPlan();
          let contentWidth = 672; // Default Letter page content width
          let dynamicLineHeight = 19.2; // Default line height
          
          if (layoutPlan?.pages?.[0]?.frames?.[0]) {
            const frame = layoutPlan.pages[0].frames[0];
            // Frame width is already the content width (margins already subtracted by backend)
            contentWidth = frame.width;
            
            // Try to get line height from layout plan
            const line = layoutPlan.pages[0].frames[0].lines?.[0];
            if (line?.height) {
              dynamicLineHeight = line.height;
            }
          }
          
          lineHeight = dynamicLineHeight;
          
          // Sanity check: prevent division by zero
          if (charWidth > 0) {
            charsPerLine = Math.floor(contentWidth / charWidth);
          }
          
          console.log('[SimplifiedCaretSystem] Using cached character positions:', {
            charCount, avgCharWidth: charWidth, lineHeight, charsPerLine, contentWidth,
            source: layoutPlan ? 'layout-plan-assisted' : 'pure-cache'
          });
        }
      }
    } else {
      console.warn('[SimplifiedCaretSystem] No layout plan or cached positions available, using fallback measurements');
    }
    
    const estimatedIndex = Math.max(0, Math.floor((fallbackX - 72) / charWidth) + Math.floor((fallbackY - 72) / lineHeight) * charsPerLine);
    
    return {
      index: estimatedIndex,
      coordinates: { 
        x: fallbackX, 
        y: fallbackY, 
        space: 'CANVAS' as any, 
        precision: 'pixel',
        timestamp: performance.now()
      },
      lineIndex: Math.floor((fallbackY - 72) / lineHeight),
      runIndex: 0,
      charInRun: Math.floor((fallbackX - 72) / charWidth),
      context: {
        isWordStart: false,
        isWordEnd: false,
        isLineStart: false,
        isLineEnd: false
      },
      metadata: {
        method: 'fallback',
        confidence: 0.3,
        timing: { totalTime: 0 },
        debugInfo: { fallbackReason: 'No last known position, using document margins' }
      }
    };
  }
  
  /**
   * Destroy the caret system and cleanup resources
   */
  destroy(): void {
    if (this.blinkingSystem) {
      this.blinkingSystem.destroy();
    }
    console.log('[SimplifiedCaretSystem] System destroyed');
  }
  
}
